import React, { useEffect, useState } from 'react';
import { useFinanceiroStore } from '../store/financeiroStore';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FiEdit3, FiDownload, FiEdit2, FiCheck, FiX } from 'react-icons/fi';
import MetasVendaModal from './MetasVendaModal';
import metaVendaService from '../services/metaVenda';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';

export default function ProjecaoMensalTab() {
  const { dadosProjecaoMensal, buscarProjecaoMensal } = useFinanceiroStore();
  const [isMetasModalOpen, setIsMetasModalOpen] = useState(false);
  const [abaAtiva, setAbaAtiva] = useState('balancete'); // 'balancete' ou 'finimp'
  const [editingRow, setEditingRow] = useState(null);
  const [editData, setEditData] = useState({});
  const [isSavingInline, setIsSavingInline] = useState(false);

  useEffect(() => {
    buscarProjecaoMensal();
  }, [buscarProjecaoMensal]);

  const handleEditClick = (linha) => {
    setEditingRow(linha.mes);
    setEditData({
      saldoInicial: linha.saldoInicial || 0,
      valorMetaVenda: linha.chaveMetaVenda || 0,
      resultadoAtingido: linha.resultadoAtingido || 0,
      projecaoFinanceira: linha.projecaoFinanceira || 0,
      projecaoFinanceiraAlcancada: linha.projecaoFinanceiraAlcancada || 0,
      despesasFixas: linha.despesasFixas || 0,
      comprasNacional: linha.comprasNacional || 0,
      investimentos: linha.investimentos || 0
    });
  };

  const handleEditChange = (campo, valor) => {
    setEditData(prev => ({ ...prev, [campo]: valor }));
  };

  const handleSaveInline = async (mes) => {
    setIsSavingInline(true);
    try {
      const payload = [{
        mesReferencia: mes,
        valorMeta: Number(editData.valorMetaVenda) || 0,
        saldoInicial: Number(editData.saldoInicial) || 0,
        resultadoAlcancado: Number(editData.resultadoAtingido) || 0,
        projecaoFinanceira: Number(editData.projecaoFinanceira) || 0,
        projecaoFinanceiraAlcancada: Number(editData.projecaoFinanceiraAlcancada) || 0,
        despesasFixas: Number(editData.despesasFixas) || 0,
        comprasNacional: Number(editData.comprasNacional) || 0,
        investimentos: Number(editData.investimentos) || 0
      }];
      await metaVendaService.salvarMetasEmLote(payload);
      toast.success('Projeção salva com sucesso!');
      setEditingRow(null);
      buscarProjecaoMensal();
    } catch (error) {
      toast.error('Erro ao salvar. Tente novamente.');
      console.error(error);
    } finally {
      setIsSavingInline(false);
    }
  };

  const formatarMoeda = (valor) => {
    if (valor === undefined || valor === null) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white/95 backdrop-blur-sm p-4 border border-gray-100 shadow-lg rounded-xl min-w-[200px]">
          <p className="font-black text-gray-900 border-b border-gray-100 pb-2 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex justify-between items-center gap-4 py-1">
              <span className="flex items-center gap-2 text-xs font-bold text-gray-600">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }}></span>
                {entry.name}
              </span>
              <span className="text-sm font-black" style={{ color: entry.color }}>
                {formatarMoeda(entry.value)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderCustomLegend = (props) => {
    const { payload } = props;
    return (
      <ul className="flex flex-wrap justify-center gap-6 mt-4">
        {payload.map((entry, index) => (
          <li key={`item-${index}`} className="flex items-center gap-2 text-sm font-bold text-gray-600">
            <svg width="24" height="4" viewBox="0 0 24 4" xmlns="http://www.w3.org/2000/svg">
              <line x1="0" y1="2" x2="24" y2="2" stroke={entry.color} strokeWidth="3" strokeDasharray={entry.payload.strokeDasharray} strokeLinecap="round" />
            </svg>
            {entry.value}
          </li>
        ))}
      </ul>
    );
  };

  const formatYAxis = (value) => {
    if (value >= 1000000 || value <= -1000000) return `R$ ${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000 || value <= -1000) return `R$ ${(value / 1000).toFixed(0)}k`;
    return `R$ ${value}`;
  };

  const formatarMesAno = (str) => {
    if (!str) return 'N/A';
    if (typeof str === 'string' && str.includes('-')) {
      const parts = str.split('-');
      if (parts.length >= 2) return `${parts[1]}/${parts[0]}`;
    }
    return str;
  };

  const dadosFormatados = (dadosProjecaoMensal || []).map(item => {
    const sInicial = Number(item.saldoInicial) || 0;
    const metaVenda = Number(item.valorMetaVenda) || 0;
    const rAtingido = Number(item.resultadoAlcancado) || 0;

    // Regra dos 65% aplicada universalmente
    const projFin = metaVenda * 0.65;

    const projFinAlc = Number(item.projecaoFinanceiraAlcancada) || 0;
    const dFixas = Number(item.despesasFixas) || 0;
    const cNacional = Number(item.comprasNacional) || 0;
    const invest = Number(item.investimentos) || 0;

    // Cálculo do Finimp a Pagar
    const totalFinimp = (item.detalhesFinimp || []).reduce((acc, p) => acc + p.valorParcela, 0);
    const finimp30 = (item.detalhesFinimp || []).filter(p => typeof p.tipoFinimp === 'string' && p.tipoFinimp.includes('30')).reduce((acc, p) => acc + p.valorParcela, 0);
    const finimp70 = (item.detalhesFinimp || []).filter(p => typeof p.tipoFinimp === 'string' && p.tipoFinimp.includes('70')).reduce((acc, p) => acc + p.valorParcela, 0);

    // Subtrair o Finimp para exibir nas colunas originais
    let p30 = (Number(item.pagamentoImportacao30) || 0) - finimp30;
    let p70 = (Number(item.pagamentoImportacao70) || 0) - finimp70;

    p30 = p30 < 0 ? 0 : p30;
    p70 = p70 < 0 ? 0 : p70;

    const desemb = Number(item.desembaraco) || 0;

    // Ajuste da fórmula do Resultado Líquido
    const despesasTotais = dFixas + cNacional + invest + p30 + p70 + desemb + totalFinimp;
    const resLiquido = (sInicial + projFin) - despesasTotais;

    return {
      ...item,
      chaveMes: formatarMesAno(item.mes || item.mesAno || item.data),
      chaveCusto: Number(item.valorFobTotal) || 0,
      chaveMeta: Number(item.metaFaturamentoTotal) || 0,
      chaveMetaVenda: metaVenda,
      saldoInicial: sInicial,
      resultadoAtingido: rAtingido,
      projecaoFinanceira: projFin,
      projecaoFinanceiraAlcancada: projFinAlc,
      despesasFixas: dFixas,
      comprasNacional: cNacional,
      pagamentoImportacao30: p30,
      pagamentoImportacao70: p70,
      finimpAPagar: totalFinimp,
      desembaraco: desemb,
      investimentos: invest,
      despesasTotais: despesasTotais,
      resultadoLiquido: resLiquido,
      detalhesFinimp: item.detalhesFinimp || []
    };
  });

  const exportarParaExcel = () => {
    const dadosParaExcel = dadosFormatados.map((linha) => ({
      "Mês/Ano": linha.chaveMes,
      "Saldo Inicial (R$)": linha.saldoInicial,
      "Meta de Vendas (R$)": linha.chaveMetaVenda,
      "Resultado Atingido (R$)": linha.resultadoAtingido,
      "Projeção Financeira (R$)": linha.projecaoFinanceira,
      "Projeção Fin. Alcançada (R$)": linha.projecaoFinanceiraAlcancada,
      "Despesas Fixas (R$)": linha.despesasFixas,
      "Boletos Compras Nacionais (R$)": linha.comprasNacional,
      "Pgto Importação 30% (R$)": linha.pagamentoImportacao30,
      "Pgto Importação 70% (R$)": linha.pagamentoImportacao70,
      "Desembaraço (R$)": linha.desembaraco,
      "Finimp a Pagar (R$)": linha.finimpAPagar,
      "Investimentos (R$)": linha.investimentos,
      "Resultado Líquido (R$)": linha.resultadoLiquido
    }));

    const worksheet = XLSX.utils.json_to_sheet(dadosParaExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Planejamento Mensal");
    XLSX.writeFile(workbook, "Planejamento_Mensal_Contabil.xlsx");
  };

  const totalResultado = dadosFormatados.reduce((acc, curr) => acc + curr.resultadoAtingido, 0);
  const totalMeta = dadosFormatados.reduce((acc, curr) => acc + curr.chaveMetaVenda, 0);
  const totalDespesas = dadosFormatados.reduce((acc, curr) => acc + curr.despesasTotais, 0);
  const totalLiquido = dadosFormatados.reduce((acc, curr) => acc + curr.resultadoLiquido, 0);

  return (
    <div className="space-y-8 animate-in fade-in duration-500 relative">
      <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <h2 className="text-lg font-semibold text-slate-900">
            Planejamento e Projeção Financeira
          </h2>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={exportarParaExcel}
              className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-md font-medium border border-slate-800 hover:bg-slate-800 transition-colors shadow-sm active:scale-95 text-sm"
            >
              <FiDownload size={18} /> Exportar Excel
            </button>

            <button
              onClick={() => setIsMetasModalOpen(true)}
              className="flex items-center gap-2 bg-white text-slate-700 px-4 py-2 rounded-md font-medium border border-slate-200 hover:bg-slate-50 transition-colors shadow-sm active:scale-95 text-sm"
            >
              <FiEdit3 size={18} /> Editar Planejamento Mensal
            </button>
          </div>
        </div>

        {dadosFormatados.length === 0 ? (
          <div className="h-[400px] w-full flex flex-col items-center justify-center bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
            <div className="text-gray-300 text-6xl mb-4">📊</div>
            <p className="text-gray-600 font-bold text-lg mb-1">Nenhum dado projetado encontrado</p>
            <p className="text-gray-400 text-sm">Realize simulações ou preencha as metas para ver o gráfico.</p>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="px-5 py-4 rounded-md border border-slate-200 bg-white flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Resultado Atingido</span>
                <span className="text-2xl font-bold text-indigo-600 tracking-tight">{formatarMoeda(totalResultado)}</span>
              </div>
              <div className="px-5 py-4 rounded-md border border-slate-200 bg-white flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Resultado Líquido</span>
                <span className="text-2xl font-bold text-emerald-600 tracking-tight">{formatarMoeda(totalLiquido)}</span>
              </div>
              <div className="px-5 py-4 rounded-md border border-slate-200 bg-white flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Despesas</span>
                <span className="text-2xl font-bold text-red-500 tracking-tight">{formatarMoeda(totalDespesas)}</span>
              </div>
              <div className="px-5 py-4 rounded-md border border-slate-200 bg-white flex flex-col gap-1 opacity-70">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Meta Histórica</span>
                <span className="text-xl font-bold text-slate-500 tracking-tight">{formatarMoeda(totalMeta)}</span>
              </div>
            </div>

            <div className="h-[380px] w-full">
              <ResponsiveContainer width="100%" height={380}>
                <LineChart
                  data={dadosFormatados}
                  margin={{ top: 10, right: 20, left: 20, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                  <XAxis dataKey="chaveMes" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 11, fontWeight: 'bold' }} dy={10} />
                  <YAxis
                    tickFormatter={formatYAxis}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#9CA3AF', fontSize: 11, fontWeight: 'bold' }}
                    dx={-10}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#E5E7EB', strokeWidth: 1, strokeDasharray: '5 5', fill: 'transparent' }} />
                  <Legend content={renderCustomLegend} />

                  <Line type="monotone" dataKey="resultadoAtingido" name="Resultado Atingido" stroke="#2563EB" strokeWidth={3} dot={{ r: 4, fill: '#2563EB', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="resultadoLiquido" name="Resultado Líquido" stroke="#10B981" strokeWidth={3} dot={{ r: 4, fill: '#10B981', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="despesasTotais" name="Total de Despesas" stroke="#EF4444" strokeWidth={3} dot={{ r: 4, fill: '#EF4444', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="chaveMetaVenda" name="Meta de Vendas" stroke="#9CA3AF" strokeWidth={2} strokeDasharray="5 5" dot={false} activeDot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-4 md:px-6 py-4 border-b border-gray-100 bg-gray-50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex gap-4">
            <button
              onClick={() => setAbaAtiva('balancete')}
              className={`font-semibold text-sm px-4 py-2 rounded-md transition-colors ${abaAtiva === 'balancete' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-100'}`}
            >
              Balancete Consolidado
            </button>
            <button
              onClick={() => setAbaAtiva('finimp')}
              className={`font-semibold text-sm px-4 py-2 rounded-md transition-colors ${abaAtiva === 'finimp' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-100'}`}
            >
              Detalhamento Finimp a Pagar
            </button>
          </div>
          {abaAtiva === 'balancete' && <span className="text-xs font-semibold text-gray-500 bg-gray-200 px-3 py-1 rounded-full text-center hidden md:block">Deslize para ver mais colunas &rarr;</span>}
        </div>

        {abaAtiva === 'balancete' ? (
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-left border-collapse min-w-[1500px]">
              <thead>
                <tr className="bg-gray-50 text-slate-500 text-xs uppercase tracking-wider">
                  <th className="px-4 py-3 font-semibold border-b border-gray-200 sticky left-0 bg-gray-50 z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">Mês / Ações</th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-200">Saldo Inicial</th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-200 text-emerald-600">Meta Venda</th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-200">Result. Atingido</th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-200">Proj. Fin. (65%)</th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-200">Proj. Fin. Alc.</th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-200 text-red-500">Despesas Fixas</th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-200 text-red-500">Boletos Compras Nacionais</th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-200 text-amber-600">Pgt Imp. 30%</th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-200 text-amber-600">Pgt Imp. 70%</th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-200 text-amber-600">Desembaraço</th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-200 text-orange-600">Finimp a Pagar</th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-200">Investimentos</th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-200 text-right bg-indigo-50/50">Res. Líquido</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {dadosFormatados && dadosFormatados.length > 0 ? (
                  dadosFormatados.map((linha, index) => {
                    const isProfitable = linha.resultadoLiquido >= 0;
                    const isEditing = editingRow === linha.mes;

                    return (
                      <tr key={index} className="hover:bg-gray-50 transition-colors text-sm">
                        <td className="px-4 py-4 font-bold text-gray-900 sticky left-0 bg-white shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)] z-20">
                          <div className="flex items-center gap-2">
                            {isEditing ? (
                              <div className="flex bg-gray-100 rounded-md p-1 shadow-inner gap-1">
                                <button onClick={() => handleSaveInline(linha.mes)} disabled={isSavingInline} className="text-emerald-600 hover:bg-emerald-200 p-1 rounded-sm transition-colors"><FiCheck size={16} /></button>
                                <button onClick={() => setEditingRow(null)} className="text-red-500 hover:bg-red-200 p-1 rounded-sm transition-colors"><FiX size={16} /></button>
                              </div>
                            ) : (
                              <button onClick={() => handleEditClick(linha)} className="text-slate-400 hover:text-slate-700 bg-transparent hover:bg-slate-100 border border-transparent hover:border-slate-200 p-1.5 rounded-md transition-all"><FiEdit2 size={14} /></button>
                            )}
                            {linha.chaveMes}
                          </div>
                        </td>

                        <td className="px-4 py-4 text-gray-600 font-mono">
                          {isEditing ? <input type="number" className="w-24 px-2 py-1 border border-emerald-300 rounded focus:ring-2 focus:ring-emerald-500 outline-none text-right" value={editData.saldoInicial} onChange={(e) => handleEditChange('saldoInicial', e.target.value)} /> : formatarMoeda(linha.saldoInicial)}
                        </td>

                        <td className={`px-4 py-4 font-mono font-bold ${linha.chaveMetaVenda > 0 && !isEditing ? 'text-emerald-600' : 'text-gray-400'}`}>
                          {isEditing ? <input type="number" className="w-24 px-2 py-1 border border-emerald-300 rounded focus:ring-2 focus:ring-emerald-500 outline-none text-right" value={editData.valorMetaVenda} onChange={(e) => handleEditChange('valorMetaVenda', e.target.value)} /> : formatarMoeda(linha.chaveMetaVenda)}
                        </td>

                        <td className="px-4 py-4 text-gray-600 font-mono">
                          {isEditing ? <input type="number" className="w-24 px-2 py-1 border border-emerald-300 rounded focus:ring-2 focus:ring-emerald-500 outline-none text-right" value={editData.resultadoAtingido} onChange={(e) => handleEditChange('resultadoAtingido', e.target.value)} /> : formatarMoeda(linha.resultadoAtingido)}
                        </td>

                        <td className="px-4 py-4 text-slate-600 font-mono">
                          {isEditing ? <input type="number" className="w-24 px-2 py-1 border border-slate-300 rounded focus:ring-2 focus:ring-slate-400 outline-none text-right" value={editData.projecaoFinanceira} onChange={(e) => handleEditChange('projecaoFinanceira', e.target.value)} /> : <span className="font-medium text-slate-700">{formatarMoeda(linha.projecaoFinanceira)}</span>}
                        </td>

                        <td className="px-4 py-4 text-gray-600 font-mono">
                          {isEditing ? <input type="number" className="w-24 px-2 py-1 border border-emerald-300 rounded focus:ring-2 focus:ring-emerald-500 outline-none text-right" value={editData.projecaoFinanceiraAlcancada} onChange={(e) => handleEditChange('projecaoFinanceiraAlcancada', e.target.value)} /> : formatarMoeda(linha.projecaoFinanceiraAlcancada)}
                        </td>

                        {/* DESPESAS FIXAS: Agora vem da automação - Bloqueado para edição */}
                        <td className="px-4 py-4 text-red-600 font-mono">
                          <span className="font-medium">{formatarMoeda(linha.despesasFixas)}</span>
                        </td>

                        {/* COMPRAS NACIONAIS: Agora vem da automação - Bloqueado para edição */}
                        <td className="px-4 py-4 text-red-600 font-mono">
                          <span className="font-medium">{formatarMoeda(linha.comprasNacional)}</span>
                        </td>

                        <td className="px-4 py-4 text-slate-600 font-mono">{formatarMoeda(linha.pagamentoImportacao30)}</td>
                        <td className="px-4 py-4 text-slate-600 font-mono">{formatarMoeda(linha.pagamentoImportacao70)}</td>
                        <td className="px-4 py-4 text-slate-600 font-mono">{formatarMoeda(linha.desembaraco)}</td>
                        <td className="px-4 py-4 text-slate-700 font-mono font-medium">{formatarMoeda(linha.finimpAPagar)}</td>

                        {/* INVESTIMENTOS: Agora vem da automação - Bloqueado para edição */}
                        <td className="px-4 py-4 text-slate-600 font-mono">
                          <span className="font-medium">{formatarMoeda(linha.investimentos)}</span>
                        </td>

                        <td className="px-4 py-4 font-mono font-semibold text-right border-l border-slate-100">
                          <span className={isProfitable && linha.resultadoLiquido !== 0 ? 'text-emerald-600' : (!isProfitable ? 'text-red-600' : 'text-slate-400')}>
                            {isProfitable && linha.resultadoLiquido !== 0 ? '+' : ''}{formatarMoeda(linha.resultadoLiquido)}
                          </span>
                        </td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan="14" className="px-6 py-10 text-center text-gray-400">Nenhum dado de projeção disponível.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto p-0">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-orange-50 text-orange-900 text-xs uppercase tracking-wider">
                  <th className="px-6 py-3 font-semibold border-b border-orange-200 whitespace-nowrap">Mês do Balancete</th>
                  <th className="px-6 py-3 font-semibold border-b border-orange-200 whitespace-nowrap">Referência</th>
                  <th className="px-6 py-3 font-semibold border-b border-orange-200 whitespace-nowrap">SKU / Importação</th>
                  <th className="px-6 py-3 font-semibold border-b border-orange-200 whitespace-nowrap">Fornecedor</th>
                  <th className="px-6 py-3 font-semibold border-b border-orange-200 whitespace-nowrap">Banco</th>
                  <th className="px-6 py-3 text-right font-semibold border-b border-orange-200 whitespace-nowrap">Valor da Parcela</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(() => {
                  const parcelas = dadosFormatados.flatMap((mesGroup) =>
                    (mesGroup.detalhesFinimp || []).map((parcela, i) => (
                      <tr key={`${mesGroup.mes}-${parcela.importacaoId || ''}-${i}`} className="hover:bg-orange-50/30 transition-colors text-sm">
                        <td className="px-6 py-4 font-bold text-gray-900 border-l-4 border-orange-500">{mesGroup.chaveMes}</td>
                        <td className="px-6 py-4">
                          <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-full ${typeof parcela.tipoFinimp === 'string' && parcela.tipoFinimp.includes('30') ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                            {parcela.tipoFinimp || 'Finimp'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-600 font-mono text-xs">{parcela.sku || `ID: ${parcela.importacaoId}`}</td>
                        <td className="px-6 py-4 text-gray-600">{parcela.fornecedor || '--'}</td>
                        <td className="px-6 py-4 text-gray-600 italic font-semibold">{parcela.banco || '--'}</td>
                        <td className="px-6 py-4 text-right font-black text-amber-700 font-mono">{formatarMoeda(parcela.valorParcela)}</td>
                      </tr>
                    ))
                  );
                  return parcelas.length === 0 ? (
                    <tr><td colSpan="6" className="px-6 py-10 text-center text-gray-400">Nenhuma parcela Finimp agendada.</td></tr>
                  ) : parcelas;
                })()}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <MetasVendaModal
        isOpen={isMetasModalOpen}
        onClose={() => setIsMetasModalOpen(false)}
        projecaoAtual={dadosProjecaoMensal}
        onMetasSalvas={buscarProjecaoMensal}
      />
    </div>
  );
}